import { LegacyFlowWarning } from "@dashboard/taxes/components";
import { Box, Checkbox, Option, RadioGroup, Select, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { Control, Controller, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { messages } from "./messages";
import { TaxesFormValues } from "./types";

export const TAXES_FORM_ID = "wizard-taxes-form";

interface TaxesFormProps {
  control: Control<TaxesFormValues>;
  disabled: boolean;
  onSubmit: (e: React.FormEvent) => void;
  strategyChoices: Option[];
  strategyChoicesLoading: boolean;
}

export const TaxesForm: React.FC<TaxesFormProps> = ({
  control,
  disabled,
  onSubmit,
  strategyChoices,
  strategyChoicesLoading,
}) => {
  const intl = useIntl();
  const chargeTaxes = useWatch({ control, name: "chargeTaxes" });

  return (
    <form id={TAXES_FORM_ID} onSubmit={onSubmit}>
      <Box display="flex" flexDirection="column" gap={4}>
        {/* Charge taxes + strategy selection */}
        <Box
          padding={4}
          borderWidth={1}
          borderStyle="solid"
          borderColor="default1"
          borderRadius={4}
        >
          <Box display="flex" alignItems="center" gap={4}>
            <Controller
              name="chargeTaxes"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Checkbox checked={value} onCheckedChange={onChange} disabled={disabled}>
                  <Text fontWeight="bold">{intl.formatMessage(messages.chargeTaxes)}</Text>
                </Checkbox>
              )}
            />
          </Box>
          <Box marginTop={3}>
            <Text size={2} color="default2">
              {intl.formatMessage(messages.taxStrategyHint)}
            </Text>
            <Controller
              name="taxCalculationStrategy"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Box display="flex" flexDirection="column" gap={2} marginTop={1}>
                  <LegacyFlowWarning taxCalculationStrategy={value} />
                  <Select
                    size="large"
                    options={strategyChoices}
                    disabled={strategyChoicesLoading || !chargeTaxes || disabled}
                    value={value}
                    onChange={onChange}
                  />
                </Box>
              )}
            />
          </Box>
        </Box>

        {/* Entered prices - radio group */}
        <Box
          padding={4}
          borderWidth={1}
          borderStyle="solid"
          borderColor="default1"
          borderRadius={4}
        >
          <Text fontWeight="bold" size={2} textTransform="uppercase" letterSpacing={3}>
            {intl.formatMessage(messages.enteredPrices)}
          </Text>
          <Box marginTop={2}>
            <Controller
              name="pricesEnteredWithTax"
              control={control}
              render={({ field: { value, onChange } }) => (
                <RadioGroup
                  value={String(value)}
                  onValueChange={val => onChange(val === "true")}
                  disabled={disabled}
                >
                  <RadioGroup.Item value="true" id="prices-with-tax" marginBottom={2}>
                    <Text>{intl.formatMessage(messages.pricesWithTaxLabel)}</Text>
                  </RadioGroup.Item>
                  <RadioGroup.Item value="false" id="prices-without-tax">
                    <Text>{intl.formatMessage(messages.pricesWithoutTaxLabel)}</Text>
                  </RadioGroup.Item>
                </RadioGroup>
              )}
            />
          </Box>
        </Box>

        {/* Rendered prices - display gross checkbox */}
        <Box
          padding={4}
          borderWidth={1}
          borderStyle="solid"
          borderColor="default1"
          borderRadius={4}
        >
          <Text fontWeight="bold" size={2} textTransform="uppercase" letterSpacing={3}>
            {intl.formatMessage(messages.renderedPrices)}
          </Text>
          <Box marginTop={2}>
            <Controller
              name="displayGrossPrices"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Checkbox checked={value} onCheckedChange={onChange} disabled={disabled}>
                  <Text>{intl.formatMessage(messages.displayGrossPrices)}</Text>
                </Checkbox>
              )}
            />
          </Box>
        </Box>
      </Box>
    </form>
  );
};
